import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SchemaOrg from '../components/SchemaOrg';

const SolucionesPage: React.FC = () => {
  const categories = [
    {
      title: "Ecosistema de Gestión",
      desc: "Centralizamos toda tu operación en un solo punto de verdad. Olvídate de buscar datos en múltiples plataformas.",
      features: ["Dashboards en tiempo real", "Integración con APIs externas", "Control de accesos jerárquicos", "Reportes exportables"]
    },
    {
      title: "Automatización Avanzada",
      desc: "Diseñamos flujos de trabajo que eliminan el error humano y aceleran el time-to-market de tus procesos internos.",
      features: ["Webhooks y disparadores", "Sincronización de bases de datos", "Bots de atención al cliente", "Facturación automatizada"]
    },
    {
      title: "IA de Negocio",
      desc: "Utilizamos modelos de lenguaje de última generación entrenados con tu propio conocimiento corporativo.",
      features: ["Búsqueda semántica en documentos", "Análisis de sentimiento de clientes", "Predicción de demanda", "Chatbots expertos en tu rubro"]
    }
  ];

  return (
    <div className="pt-32 pb-24">
      <SEO
        title="Soluciones Digitales para Pymes | Sistemas a Medida"
        description="Ecosistemas de gestión, automatización avanzada e inteligencia artificial para pymes. Desarrollamos soluciones digitales escalables que ordenan y potencian tu negocio."
        keywords="soluciones digitales pymes, software a medida, automatización empresas, inteligencia artificial negocios, sistemas de gestión, desarrollo web argentina"
        url="https://lqpglobal.com/soluciones"
      />
      <SchemaOrg type="service" />

      <div className="container mx-auto px-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-white transition-colors" itemProp="item">
                <span itemProp="name">Inicio</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <span>/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-400" itemProp="name">Soluciones</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        <header className="max-w-4xl mb-24">
          <div className="inline-block px-4 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-500 mb-6 uppercase tracking-widest">
            Nuestro Enfoque
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-gradient mb-8 leading-tight">
            Soluciones que <br /> <span className="text-red-500">Escalan</span> tu Visión
          </h1>
          <p className="text-gray-400 text-xl leading-relaxed">
            No desarrollamos herramientas aisladas; construimos ecosistemas digitales diseñados para sostener y potenciar el crecimiento de pymes modernas.
          </p>
        </header>

        <section className="grid lg:grid-cols-3 gap-8 mb-24" aria-labelledby="soluciones-title">
          <h2 id="soluciones-title" className="sr-only">Nuestras soluciones</h2>
          {categories.map((cat, i) => (
            <article key={i} className="glass p-10 rounded-[2.5rem] border-white/5 hover:border-white/20 transition-all group">
              <h3 className="text-2xl font-bold mb-6 text-white group-hover:text-red-500 transition-colors">{cat.title}</h3>
              <p className="text-gray-400 mb-8 leading-relaxed text-sm">{cat.desc}</p>
              <ul className="space-y-4" aria-label={`Características de ${cat.title}`}>
                {cat.features.map((feat, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm text-gray-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
                    {feat}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="bg-[#0a0a0a] rounded-[3rem] p-12 md:p-20 border border-white/5 relative overflow-hidden" aria-labelledby="stack-title">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5" />
          <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 id="stack-title" className="text-4xl font-bold mb-6">Stack Tecnológico 2026</h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Utilizamos las tecnologías más robustas del mercado para garantizar que tu inversión no quede obsoleta en el corto plazo.
              </p>
              <div className="flex flex-wrap gap-4">
                {["React 19", "Node.js 22", "PostgreSQL", "Google Cloud", "Gemini 2.5", "TypeScript"].map(tech => (
                  <span key={tech} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-mono text-gray-300">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="glass p-6 rounded-2xl text-center">
                <div className="text-3xl font-bold text-white mb-2">99.9%</div>
                <div className="text-xs text-gray-500 uppercase">Uptime Garantizado</div>
              </div>
              <div className="glass p-6 rounded-2xl text-center">
                <div className="text-3xl font-bold text-white mb-2">&lt;200ms</div>
                <div className="text-xs text-gray-500 uppercase">Latencia Promedio</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-24 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">¿Listo para escalar tu negocio?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Contanos tu desafío y diseñamos juntos la solución perfecta para tu empresa.
          </p>
          <Link
            to="/contacto"
            className="inline-block px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all"
          >
            Agendar Consultoría Gratuita
          </Link>
        </section>
      </div>
    </div>
  );
};

export default SolucionesPage;
